import React, { useRef, useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import styled, { css } from 'styled-components';

import ShopByColorLink from '~/layout/components/ShopByColorLink/ShopByColorLink';
import {
  SWATCH_TYPES,
  SWATCH_TYPES_LABEL_MAP,
} from '~/pdp/constants/productDetail';
import isImageSetPlusSized from '~/pdp/utils/isImageSetPlusSized';
import { useQuickViewContext } from '~/quickView/components/QuickViewContext';
import featureNames from '~/shared/constants/featureFlags';
import logger from '~/shared/utils/logger';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { swatchLabelStyle } from '~/styles/genericSelectorStyles';
import {
  AttributeFieldTypes,
  GenericProductAttributeSelector,
  ProductAttributeSelector,
  ProductSelectorType,
  useProductAttributeFieldType,
  useProductDetailSelectionState,
  useProductDetailContext,
  useErrorRegistrationContext,
  useId,
  useTheme,
  useBreakpoint,
  mobile,
  Tooltip,
  useModelImageContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

import { ProductDetailLabel } from '../ProductDetailLabels';

const debug = logger.extend('ProductDetailColorSelector');

const listStyle = css`
  margin: 0;
  gap: 4px;
`;

const itemStyle = css`
  padding: 3px 0 0 3px;
  flex-direction: column;
  justify-content: flex-start;
  gap: 4px;
`;

const pdpSwatchLabelStyle = css`
  ${swatchLabelStyle}

  font-size: 10px;
`;

const pdpEcatSwatchLabelStyle = css`
  ${pdpSwatchLabelStyle}

  max-width: 60px;
`;

const ColorSelector = styled(GenericProductAttributeSelector)`
  margin-bottom: 0px;

  ${({ $selectorScrollEnabled }) =>
    $selectorScrollEnabled &&
    mobile`
  	*::-webkit-scrollbar {
  	  -webkit-appearance: none;
  	  display: none;
  	}
    ul {
      li {
        padding-bottom: 0px;
      }
    }
  `}

  input {
    cursor: pointer;
  }

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const colorInputStyle = css`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  border: solid 1px ${({ theme }) => theme.colors.pdpColorSelectorInputBorder};
  input:checked + && {
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.focusText};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.palette.white};
  }

  input:checked:focus + && {
    box-shadow:
      inset 0 0 0 2px ${({ theme }) => theme.colors.palette.white},
      0 0 0 2px ${({ theme }) => theme.colors.pdpColorInputCheckedFocus};
  }
`;

const colorInputStyleEcatSwatch = css`
  ${colorInputStyle}

  height: 88px;
  width: 60px;
  border-radius: 0px;
  background-size: contain;

  input:checked + && {
    background-size: contain;
  }
`;

const colorInputStyleEcatSwatchForBundles = css`
  ${colorInputStyleEcatSwatch}

  height: 84px;
  width: 57px;
`;

const SelectorTextWrapper = styled.div`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-size: 14px;
`;

const itemSoldOutStyle = css`
  &:before {
    background: linear-gradient(
      to top left,
      transparent calc(50% - 1px),
      ${({ theme }) => theme.colors.white} calc(50% - 1px),
      ${({ theme }) => theme.colors.white} calc(50% + 1px),
      transparent calc(50% + 1px),
      transparent
    );
  }
`;

const selectorOverflowScrollEnabledStyle = css`
  margin-right: -24px;
  margin-left: 0px;
`;
const selectorOverflowScrollEnabledStyleForBundles = css`
  ${selectorOverflowScrollEnabledStyle}

  margin-right: -16px;
`;

const CategoryLabel = styled(ProductDetailLabel)`
  margin-bottom: 8px;
  ${({ theme }) => theme.gridCellStyles.swatches.swatchLabel};
`;

const SwatchWrapper = styled.div`
  display: block;

  ${({ theme }) => theme.gridCellStyles.swatches.swatchWrapper};
`;

const SwatchLabel = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  line-height: ${18 / 14};
  display: flex;
  align-items: center;

  ${({ theme }) => theme.gridCellStyles.swatches.swatchLabel};

  ${CategoryLabel} {
    margin: unset;
    line-height: normal;
  }
`;

const selectedSwatchTextStyle = css`
  letter-spacing: 0;

  ${({ theme }) => theme.gridCellStyles.swatches.swatchColorLabel};
`;

const TooltipAnchor = styled(Tooltip.Anchor)`
  display: inline-block;
  margin-right: 2px;
`;

const TooltipMessage = styled(Tooltip.Content)`
  background: ${({ theme: { colors } }) => colors.palette.white};
  max-width: 270px;
  padding: 16px 12px;
  border: 0.5px solid ${({ theme: { colors } }) => colors.palette.shadow30};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.05);
  z-index: 5;
  border-radius: 8px;
  text-transform: none;
  font-weight: 400;
  font-size: 12px;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.palette.black};
`;

const TooltipIcon = styled(HiOutlineInformationCircle)`
  width: 15px;
  height: 15px;
`;

const TooltipInfoIcon = styled.div`
  padding-left: 2px;
  align-item: center;
  height: 15px;
`;

const NoOptionsWrapper = styled.div`
  margin-top: 8px;
  width: fit-content;

  ${({ theme }) => theme.pdpStyles.swatchSelector.noOptionsWrapper};
`;

const SwatchLabelOOSMessage = styled.span`
  ${({ theme }) => theme.pdpStyles.swatchSelector.swatchLabelOOSMessage};
`;

const DynamicFormattedMessage = FormattedMessage;

const SwatchTooltip = () => {
  return (
    <Tooltip>
      <TooltipAnchor>
        <TooltipInfoIcon>
          <TooltipIcon />
        </TooltipInfoIcon>
      </TooltipAnchor>
      <TooltipMessage>
        <FormattedMessage
          id="site_product_page.swatch-summary-tooltip"
          defaultMessage="Psst! Our limited edtion colors and patterns are only around till they're not,
          so grab them while they're still in stock."
        />
      </TooltipMessage>
    </Tooltip>
  );
};

const ColorLabel = ({
  attribute,
  selectedOption,
  field,
  swatch,
  isSummary,
}) => {
  const { productIndex, swatchTypesAvailable } =
    useProductDetailSelectionState();
  const uniqueId = useId(`${productIndex}-${attribute.field}-`);
  const theme = useTheme();
  const isSecondarySite = useSecondarySite();
  const { selectorLabelStyle } = theme.selectorStyles;
  const errorRegistrationDisabled = Boolean(
    theme.context?.errorRegistrationDisabled
  );
  const ref = useRef();
  const hasError =
    (selectedOption && !selectedOption.isAvailable) || !selectedOption;
  useErrorRegistrationContext(
    ref,
    errorRegistrationDisabled ? false : hasError,
    uniqueId
  );
  const enableSwatchTypes =
    swatchTypesAvailable && swatchTypesAvailable.length > 1;
  const swatchTypeLabel = SWATCH_TYPES_LABEL_MAP[swatch] || swatch;
  const { enabled: isShopByColorEnabled } = useHasFeature(
    featureNames.SHOP_BY_COLOR
  );
  const displayShopByColorLink = isShopByColorEnabled && !isSecondarySite;

  return (
    <SelectorTextWrapper ref={ref}>
      {enableSwatchTypes ? (
        <SwatchLabel>
          <DynamicFormattedMessage
            id={`site_product_page.swatch_summary_type_${swatch.toLowerCase()}`}
            defaultMessage={swatchTypeLabel}
          />
          {selectedOption.swatchType === swatch && (
            <>
              {': '}
              <CategoryLabel
                attribute={attribute}
                field={field}
                infoTextStyles={selectedSwatchTextStyle}
                labelTextStyles={selectorLabelStyle}
                selectedOption={selectedOption}
                displayFilterField={false}
                isSummary={isSummary}
              />
              {displayShopByColorLink && (
                <ShopByColorLink
                  masterProductId={selectedOption.masterProductId}
                  filterByProductGender
                />
              )}
            </>
          )}
          {swatch === SWATCH_TYPES.DEFAULT && (
            <>
              <SwatchTooltip />
            </>
          )}
        </SwatchLabel>
      ) : (
        <CategoryLabel
          attribute={attribute}
          field={field}
          infoTextStyles={selectedSwatchTextStyle}
          labelTextStyles={selectorLabelStyle}
          selectedOption={selectedOption}
          isSummary={isSummary}
        />
      )}
    </SelectorTextWrapper>
  );
};

ColorLabel.propTypes = {
  attribute: PropTypes.object,
  field: PropTypes.string,
  isSummary: PropTypes.bool,
  selectedOption: PropTypes.object,
  swatch: PropTypes.string,
};

function ProductDetailColorSelector({
  relatedProductsLoading,
  disabled,
  disableAllOptions,
  ...rest
}) {
  const quickViewContext = useQuickViewContext();
  const isQuickView = quickViewContext?.isQuickView;
  const { isMobile } = useBreakpoint();
  const colorField = useProductAttributeFieldType(AttributeFieldTypes.COLOR);
  const { swatchTypesAvailable, isPlusSizeSelected } =
    useProductDetailSelectionState();
  const { isBundle, selectionStateByProductIndex } = useProductDetailContext();
  const useEcatSwatches = config.get('public.brand.features.useEcatSwatches');

  const { activeModelImages } = useModelImageContext();

  const { selectedAttributes, attributeFieldTypes } =
    selectionStateByProductIndex[0];
  const selectedSize = selectedAttributes[attributeFieldTypes.SIZE];

  const enableSwatchTypes =
    swatchTypesAvailable && swatchTypesAvailable.length > 1;
  const isSelectorScrollEnabled = config.get(
    'public.brand.features.isColorSelectorScrollEnabled'
  );
  // if we're in outfits then always use selected size
  // otherwise align with pdp model being shown
  const isPlusModel =
    isBundle && selectedSize
      ? isPlusSizeSelected
      : isImageSetPlusSized(activeModelImages);
  const eCatImageSet = isPlusModel
    ? ['urlEcatPlus', 'urlEcatDefault']
    : ['urlEcatDefault'];
  const eCatStyleToUse = isBundle
    ? colorInputStyleEcatSwatchForBundles
    : colorInputStyleEcatSwatch;
  const pdpSwatchLabelStyleToUse = useEcatSwatches
    ? pdpEcatSwatchLabelStyle
    : pdpSwatchLabelStyle;
  const swatchImageSet = useEcatSwatches ? eCatImageSet : ['url'];
  const selectorOverflowStyleToUse = isBundle
    ? selectorOverflowScrollEnabledStyleForBundles
    : selectorOverflowScrollEnabledStyle;
  const colorSelectorInputStyle = useEcatSwatches
    ? eCatStyleToUse
    : colorInputStyle;

  const selectorScrollEnabled =
    (isSelectorScrollEnabled && isMobile) || isQuickView;

  const getRadioButtonProps = useCallback(
    (option) => {
      return {
        'data-autotag': option.isAvailable
          ? 'pdp-saleable-color'
          : 'pdp-sold-out-color',
        disabled,
      };
    },
    [disabled]
  );

  const swatchSummary = useCallback(
    (attribute, selectedOption, swatch) => {
      return (
        <ColorLabel
          attribute={attribute}
          selectedOption={selectedOption}
          field={colorField}
          swatch={swatch}
          isSummary
        />
      );
    },
    [colorField]
  );

  const { formatMessage } = useIntl();
  const ariaPrefix = formatMessage({
    id: 'pdp_attribute_selector.aria_prefix_color',
    defaultMessage: 'color',
  });

  let swatchTypes = [''];
  if (enableSwatchTypes) {
    swatchTypesAvailable.sort();
    const untaggedIndex = swatchTypesAvailable.indexOf(null);

    if (untaggedIndex !== -1) {
      swatchTypesAvailable.splice(untaggedIndex, 1);
      swatchTypesAvailable.push(SWATCH_TYPES.DEFAULT);
    }
    swatchTypes = swatchTypesAvailable;
  }

  return (
    <SwatchWrapper>
      {swatchTypes.map((swatch, index) => {
        return (
          <ProductAttributeSelector
            field={colorField}
            isLoading={relatedProductsLoading}
            key={index}
          >
            {({ attribute, selectedOption, options }) => {
              // TODO: revisit why `selectedOption` is ever null
              if (!selectedOption) {
                debug('No selected option found.', attribute);
                return null;
              }

              const availableSwatchesByCurrentSwatchType = options.filter(
                ({ swatchType, isAvailable }) =>
                  swatchType === swatch && isAvailable
              );

              const isSelectedByCurrentSwatchType = options.some(
                ({ swatchType, value }) =>
                  swatchType === swatch && value === selectedOption.value
              );

              const swatchTypeLabel = SWATCH_TYPES_LABEL_MAP[swatch] || swatch;

              if (
                enableSwatchTypes &&
                !availableSwatchesByCurrentSwatchType.length &&
                !isSelectedByCurrentSwatchType
              ) {
                return (
                  <>
                    <SwatchLabel>
                      <DynamicFormattedMessage
                        id={`site_product_page.swatch_summary_type_${swatch.toLowerCase()}`}
                        defaultMessage={swatchTypeLabel}
                      />
                      <SwatchLabelOOSMessage>
                        {' '}
                        -{' '}
                        <FormattedMessage
                          id="site_product_page.swatch_summary_no_options_available"
                          defaultMessage="Out of Stock"
                        />
                      </SwatchLabelOOSMessage>
                    </SwatchLabel>
                    <NoOptionsWrapper>
                      <FormattedMessage
                        id="site_product_page.no_options_available"
                        defaultMessage="No options available"
                      />
                    </NoOptionsWrapper>
                  </>
                );
              }

              return (
                <ColorSelector
                  ariaPrefix={ariaPrefix}
                  displayCheckedAndUnavailable
                  displayCheckedAndSoldOut
                  displaySoldOutItem={false}
                  hideLabel
                  inputStyle={colorSelectorInputStyle}
                  itemSoldOutStyle={itemSoldOutStyle}
                  selectorScrollEnabled={selectorScrollEnabled}
                  $selectorScrollEnabled={selectorScrollEnabled}
                  selectorOverflowScrollEnabledStyle={
                    selectorOverflowStyleToUse
                  }
                  selectorType={ProductSelectorType.SWATCH}
                  swatchType={swatch}
                  swatchImageKeysToUse={swatchImageSet}
                  renderSummary={() =>
                    swatchSummary(attribute, selectedOption, swatch)
                  }
                  getRadioButtonProps={getRadioButtonProps}
                  data-autotag="pdp-color-group"
                  disabled={disabled}
                  hideOverflowIndicators
                  itemStyle={itemStyle}
                  itemLabelStyle={pdpSwatchLabelStyleToUse}
                  listStyle={listStyle}
                  disableAllOptions={disableAllOptions}
                  isQuickView={isQuickView}
                  {...rest}
                />
              );
            }}
          </ProductAttributeSelector>
        );
      })}
    </SwatchWrapper>
  );
}

ProductDetailColorSelector.propTypes = {
  disableAllOptions: PropTypes.bool,
  disabled: PropTypes.bool,
  relatedProductsLoading: PropTypes.bool,
};

export default ProductDetailColorSelector;
